import { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import { useSchoolFee } from "../../context/schoolFeeContext";
import { formatCurrency } from "../../utils";
import {
  amountKeystrokes,
  isWithinFee,
  toMinorUnits,
} from "../../utils/schoolFeeAmount";
import type { ServiceAmountBounds } from "../../utils/schoolFeeAmount";

/**
 * PAY-06 / PAY-07. The amount the payer will be charged.
 *
 * ⛔ `amount` is MANDATORY on the pay request (D-HARM-16) -- the server no
 * longer reads an absent amount as "the full fee", so the form always carries a
 * value and is pre-filled with the fee rather than relying on a default that
 * does not exist any more.
 *
 * ⛔ Nothing here parses the money into a float to decide whether it may be
 * paid. `isWithinFee` compares integer minor units, because a fee ceiling
 * compared as a float is an approximate ceiling and the ceiling is the only
 * thing between a partial payment and an overpayment.
 */
const SchoolFeeAmountForm = ({
  serviceBounds,
}: {
  serviceBounds?: ServiceAmountBounds;
} = {}) => {
  const { t } = useTranslation();
  const { selectedFee, amount, setAmount } = useSchoolFee();

  const fee = selectedFee?.amount ?? "";
  // ⚠ Only an EXPLICIT false refuses a partial payment. Journey A's fees come
  // from the student-search hit and always carry the flag; journey B's come
  // from /schoolfee/fee-assignment, which carries it from customer portal API
  // 2.1.0 and omits it before. Reading undefined as false would block every
  // partial payment on an older server, on no evidence that the school
  // forbids one -- and the server refuses a partial it does not allow
  // (CPS-422-111) either way. Unknown therefore defers to the server.
  const partialAllowed = selectedFee?.supports_partial_payments !== false;

  // Pre-filled with the full fee, ONCE PER FEE, tracked by the fee's own id.
  //
  // ⛔ Not "whenever the field is empty". That reads as the same rule and is
  // not: clearing the box to type a partial amount empties it, so the effect
  // re-filled it on the next render and the payer could never enter anything
  // but the full fee. Caught by the partial-payment test, which is the only
  // thing between that and a payer who cannot pay what they can afford.
  const prefilledFor = useRef<string | null>(null);

  useEffect(() => {
    const feeId = selectedFee?.fee_assignment_id ?? null;

    if (!feeId || !fee || prefilledFor.current === feeId) return;

    prefilledFor.current = feeId;
    setAmount(fee);
  }, [selectedFee, fee, setAmount]);

  const invalid = amount !== "" && !isWithinFee(amount, fee, serviceBounds);

  const entered = amount !== "" ? toMinorUnits(amount) : null;
  const feeCeiling = toMinorUnits(fee);
  const serviceMin =
    typeof serviceBounds?.min === "number" && serviceBounds.min > 0
      ? serviceBounds.min
      : undefined;
  const serviceMax =
    typeof serviceBounds?.max === "number" && serviceBounds.max > 0
      ? serviceBounds.max
      : undefined;

  const exceedsFee =
    invalid && entered !== null && feeCeiling !== null && entered > feeCeiling;
  const belowServiceMin =
    invalid &&
    !exceedsFee &&
    entered !== null &&
    serviceMin !== undefined &&
    entered < Math.round(serviceMin * 100);
  const aboveServiceMax =
    invalid &&
    !exceedsFee &&
    !belowServiceMin &&
    entered !== null &&
    serviceMax !== undefined &&
    entered > Math.round(serviceMax * 100);

  // A service that does not support partial payment is paid in full or not at
  // all, so anything short of the fee is refused here rather than at the server.
  // The step's gate applies the same rule, so the two cannot disagree.
  const partialRefused =
    !partialAllowed &&
    !invalid &&
    amount !== "" &&
    !isWithinFee(amount, fee, serviceBounds, { partialAllowed: false });

  const helper = belowServiceMin
    ? t("schoolfee.amount.below_service_min", {
        min: formatCurrency(serviceMin ?? 0, undefined, selectedFee?.currency),
      })
    : aboveServiceMax
    ? t("schoolfee.amount.above_service_max", {
        max: formatCurrency(serviceMax ?? 0, undefined, selectedFee?.currency),
      })
    : invalid
    ? t("schoolfee.amount.invalid")
    : partialRefused
    ? t("schoolfee.amount.partial_unsupported")
    : partialAllowed
    ? t("schoolfee.amount.maximum", {
        max: formatCurrency(fee, undefined, selectedFee?.currency),
      })
    : t("schoolfee.amount.full");

  return (
    <Box mb={4}>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        {t("schoolfee.steps.amount")}
      </Typography>

      <TextInput
        fullWidth
        type="text"
        inputMode="decimal"
        id="schoolfee-amount"
        label={t("schoolfee.amount.label")}
        value={amount}
        // Filtered at the keystroke rather than corrected on submit: a third
        // decimal place is refused by the server, and a payer should never see
        // a number they typed silently rewritten.
        onChange={event => setAmount(amountKeystrokes(event.target.value))}
        error={invalid || partialRefused}
        helperText={helper}
        variant="outlined"
      />
    </Box>
  );
};

export default SchoolFeeAmountForm;
